import { EventBusProps, eventBusInstance } from '@moodys/cre-cpm.models.events';
import { AppId, RemoteId } from '@moodys/cre-cpm.models.micro-frontends';
import { getMicrofrontends } from '@moodys/cre-cpm.functions.micro-frontends';
import { VerticalSlicedMicroFrontendRoutes } from '@moodys/cre-cpm.constants.micro-frontends';
import { getLocationPath } from '@moodys/cre-cpm.functions.window-location';
import { LogMessage } from '@moodys/cre-cpm.models.logging';

export type AsyncFunction<T extends unknown[], R> = (...args: T) => Promise<R>

export const sentErrorToEventBus = (
  remoteId: RemoteId,
  error: unknown,
  context?: unknown,
  eventBus: EventBusProps = eventBusInstance,
): void => {
  eventBus.publish<LogMessage>('RXD_LOG', { remoteId, value: error, context, level: "error" });
};

export interface ExecuteErrorHandlingOptions {
  remoteId: RemoteId
  throw?: boolean
  context?: unknown
  eventBus?: EventBusProps
}

export const executeWithErrorHandling = <T extends (...args: any[]) => (any | undefined)>
  (
    fn: T,
  ): (
    options: ExecuteErrorHandlingOptions,
    ...args: Parameters<T>
  ) => ReturnType<T> | undefined => (
    options: ExecuteErrorHandlingOptions,
    ...args: Parameters<T>
  ): ReturnType<T> | undefined => {
    try {
      return fn(...args);
    } catch (error) {
      sentErrorToEventBus(options.remoteId, error, options.context, options.eventBus);

      if (options.throw) {
        throw error;
      }
    }
    return undefined;
  };

export const executeWithErrorHandlingAsync = <T extends unknown[], R>(fn: AsyncFunction<T, R>) =>
    async (options: ExecuteErrorHandlingOptions, ...args: T): Promise<R | undefined> => {
    try {
      return await fn(...args);
    } catch (error) {
      sentErrorToEventBus(options.remoteId, error, options.context, options.eventBus);
      if (options.throw) {
        throw error;
      }
      return undefined;
    }
  };

export const tryLocateErrorByFileOrigin = (err: any, filename?: string): AppId | undefined => {
  if ((err instanceof ErrorEvent && err.filename) || filename) {
    const apps = (getMicrofrontends() as AppId[]).concat('hubApp');
    return apps.find((appId) => (err.filename ?? filename).indexOf(appId) !== -1);
  }
  return undefined;
};

export const tryLocateErrorByStack = (err: any): AppId | undefined => {
  if (err && err instanceof Error && err.stack) {
    const apps = (getMicrofrontends() as AppId[]).concat('hubApp');
    return apps.find((remoteId) => err?.stack?.indexOf(remoteId) !== -1);
  }
  return undefined;
};

export const tryLocateErrorByRoute = (): AppId => {
  const routePath = getLocationPath();
  if (routePath.indexOf(VerticalSlicedMicroFrontendRoutes.quicksight) !== -1) {
    return 'quicksightApp';
  } if (routePath.indexOf(VerticalSlicedMicroFrontendRoutes.platform) !== -1) {
    return 'platformApp';
  } if (routePath.indexOf(VerticalSlicedMicroFrontendRoutes.loans) !== -1) {
    return 'loansApp';
  } if (routePath.indexOf(VerticalSlicedMicroFrontendRoutes.map) !== -1) {
    return 'mapApp';
  }
  if (
    routePath.indexOf(VerticalSlicedMicroFrontendRoutes.notifications) !== -1
  ) {
    return 'notificationCenterApp';
  }
  if (
    routePath.indexOf(VerticalSlicedMicroFrontendRoutes.portfolioAssistant) !==
    -1
  ) {
    return 'assistantApp';
  }
  return 'hubApp';
};
